import React, { useState, useEffect, useCallback } from "react";
import { View, StyleSheet, Text } from "react-native";
import { router } from "expo-router";
import { ChannelList } from "stream-chat-expo";
import { getStreamClient } from "@/services/stream/client";
import { ChannelPreviewAvatar } from "@/components/chat/ChannelPreviewAvatar";
import { useIsTablet } from "@/hooks/useIsTablet";
import { ChannelView } from "@/components/chat/ChannelView";
import type { UniverseStreamChannel } from "@/services/stream/types";
import { navigationEvents, NAVIGATION_EVENT_TYPES } from "@/utils/navigationEvents";
import { ChannelListHeader } from "@/components/chat/ChannelListHeader";
import type { FilterOption } from "@/components/chat/ChannelFilterDropdown";

const STATUS_FILTER_MAP: Partial<Record<FilterOption, string>> = {
    'Active': 'active',
    // The backend stores WaConversations::IN_CONVERSATION as 'in conversation'
    // (with a space) in the channel's custom_data.status.
    'In Conversation': 'in conversation',
    'Pending': 'pending',
    'Inactive': 'inactive',
};


export default function InboxScreen() {
    const client = getStreamClient();
    const userId = client.userID;
    const isTablet = useIsTablet();
    const [selectedChannel, setSelectedChannel] = useState<UniverseStreamChannel | null>(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedFilter, setSelectedFilter] = useState<FilterOption>('All');

    useEffect(() => {
        if (!isTablet) return;

        const subscription = navigationEvents.addListener(
            NAVIGATION_EVENT_TYPES.SELECT_CHANNEL,
            async ({ channelId }: { channelId: string }) => {
                try {
                    const filters = { id: channelId };
                    const channels = await client.queryChannels(filters);
                    if (channels.length > 0) {
                        setSelectedChannel(channels[0] as UniverseStreamChannel);
                    }
                } catch (error) {
                    console.error("Error selecting channel from event:", error);
                }
            }
        );

        return () => {
            subscription.remove();
        };
    }, [isTablet, client]);

    if (!userId) return null;

    const onChannelSelect = (channel: any) => {
        if (isTablet) {
            setSelectedChannel(channel as UniverseStreamChannel);
        } else {
            router.push(`/chat/${channel.id}`);
        }
    };

    const statusValue = STATUS_FILTER_MAP[selectedFilter];
    const filters = {
        type: "messaging",
        members: { $in: [userId] },
        ...(searchQuery ? { name: { $autocomplete: searchQuery } } : {}),
    };

    const queryChannelsOverride = useCallback(
        async (...args: Parameters<typeof client.queryChannels>) => {
            const channels = await client.queryChannels(...args);
            if (!statusValue) return channels;
            return channels.filter((ch) => ch.data?.custom_data?.status === statusValue);
        },
        [client, statusValue],
    );

    if (isTablet) {
        return (
            <View style={styles.tabletContainer}>
                <View style={styles.listContainer}>
                    <ChannelListHeader onSearch={setSearchQuery} onFilterChange={setSelectedFilter} />
                    <ChannelList
                        key={selectedFilter}
                        filters={filters}
                        sort={{ last_message_at: -1 }}
                        onSelect={onChannelSelect}
                        PreviewAvatar={ChannelPreviewAvatar}
                        queryChannelsOverride={queryChannelsOverride}
                    />
                </View>
                <View style={styles.chatContainer}>
                    {selectedChannel ? (
                        <ChannelView channel={selectedChannel} isTablet={true} />
                    ) : (
                        <View style={styles.noChannelSelected}>
                            <Text style={styles.noChannelText}>Select a chat to start messaging</Text>
                        </View>
                    )}
                </View>
            </View>
        );
    }

    return (
        <View style={styles.mobileContainer}>
            <ChannelListHeader onSearch={setSearchQuery} onFilterChange={setSelectedFilter} />
            <ChannelList
                key={selectedFilter}
                filters={filters}
                sort={{ last_message_at: -1 }}
                onSelect={onChannelSelect}
                PreviewAvatar={ChannelPreviewAvatar}
                queryChannelsOverride={queryChannelsOverride}
            />
        </View>
    );
}

const styles = StyleSheet.create({
    tabletContainer: {
        flex: 1,
        flexDirection: 'row',
        backgroundColor: 'white',
    },
    mobileContainer: {
        flex: 1,
        backgroundColor: 'white',
    },
    listContainer: {
        width: 350,
        borderRightWidth: StyleSheet.hairlineWidth,
        borderRightColor: '#E5E5E5',
    },
    chatContainer: {
        flex: 1,
        backgroundColor: '#f8fbff',
    },
    noChannelSelected: {
        flex: 1,
        justifyContent: 'center',
        alignItems: 'center',
    },
    noChannelText: {
        fontSize: 18,
        color: '#9CA3AF',
    },
});
